import { Bill, BillSource, BillSortValue } from "@common/types/bill";
import { BillColumn, BillView } from "@common/types/views/billViews";
import { View } from "@common/types/views/views";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useAccrualEntityIdsWithLoading } from "@api/ledger.api";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import type { ViewTableLoadMore } from "@design-system/ViewTable/ViewTableLoadMore";
import { PeriodLockDatesProvider } from "@components/periodLock/PeriodLockDatesProvider";
import {
    BillScheduleWiring,
    resolveBillScheduleColumns,
    SCHEDULE_HANDOFF_COLUMN_OPTIONS,
} from "@components/bills/schedules/billScheduleColumns";
import { useBillScheduleTableOptions } from "@components/bills/schedules/useBillScheduleTableOptions";
import { hideAccrualOnlyColumns } from "../billAccrualColumns";
import { useBillMatchTableOptions } from "../useBillMatchTableOptions";
import { createBillsViewConfig } from "./billsViewConfig";
import styles from "./BillsView.module.scss";

const isImporterBill = (bill: Bill) => bill.source === BillSource.IMPORTER;

interface Props {
    view: BillView;
    bills: Bill[] | null;
    selectedBillId?: string;
    onBillClick?: (bill?: Bill) => void;
    sort?: BillSortValue;
    onSortChange?: (sort: BillSortValue) => void;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    hasAnyAccrualLedgers?: boolean;
    /** Which rows can be selected. Defaults to importer bills, the only ones the edit drawer accepts. */
    canSelectBill?: (bill: Bill) => boolean;
    onViewChange?: (view: View) => void;
    scheduleWiring?: BillScheduleWiring;
    loadMore?: ViewTableLoadMore;
}

const SCHEDULE_HANDOFF_COLUMN: BillView["columns"][number] = {
    id: BillColumn.SCHEDULE,
    options: SCHEDULE_HANDOFF_COLUMN_OPTIONS,
};

export const BillsView: React.FC<Props> = ({
    view,
    bills,
    selectedBillId,
    onBillClick,
    sort,
    onSortChange,
    emptyView = () => "No bills found",
    footer,
    className,
    rowSelection,
    onRowSelectionChange,
    hasAnyAccrualLedgers = false,
    canSelectBill = isImporterBill,
    onViewChange,
    scheduleWiring,
    loadMore,
}) => {
    const data = bills ?? null;

    const handleRowClick = useCallback(
        (bill?: Bill) => {
            onBillClick?.(bill);
        },
        [onBillClick],
    );

    const handleFocusedRowChange = useCallback(
        (
            _index: number | null,
            row: Bill | null,
            source: "keyboard" | "click",
        ) => {
            if (
                source !== "keyboard" ||
                selectedBillId === undefined ||
                !row ||
                row.id === selectedBillId
            ) {
                return;
            }
            onBillClick?.(row);
        },
        [selectedBillId, onBillClick],
    );

    const entityIds = useMemo(
        () => bills?.map((bill) => bill.entityId) ?? [],
        [bills],
    );
    const { accrualEntityIds, isLoading: isLoadingAccrualLedgers } =
        useAccrualEntityIdsWithLoading(entityIds);

    const scheduleOptions = useBillScheduleTableOptions(scheduleWiring);
    const scheduleCallbacks = scheduleWiring?.callbacks;
    const billIds = useMemo(() => bills?.map((bill) => bill.id) ?? [], [bills]);
    const isMatchColumnVisible =
        hasAnyAccrualLedgers &&
        view.columns.some((column) => column.id === BillColumn.MATCH);
    const matchOptions = useBillMatchTableOptions({
        billIds,
        isMatchColumnVisible,
    });
    const isReadonly = view.options?.readonly;
    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onBillClick,
                ...scheduleCallbacks,
            },
            options: {
                ...scheduleOptions,
                ...matchOptions,
                readonly: isReadonly,
            },
            accrualEntityIds,
            isLoadingAccrualLedgers,
        }),
        [
            onBillClick,
            scheduleCallbacks,
            scheduleOptions,
            matchOptions,
            isReadonly,
            accrualEntityIds,
            isLoadingAccrualLedgers,
        ],
    );

    const isSchedulesEnabled = scheduleWiring != null;
    const viewConfig = useMemo(
        () =>
            createBillsViewConfig({ hasAnyAccrualLedgers, isSchedulesEnabled }),
        [hasAnyAccrualLedgers, isSchedulesEnabled],
    );
    const visibleView = useMemo(
        () => ({
            ...view,
            columns: resolveBillScheduleColumns({
                columns: hideAccrualOnlyColumns({
                    columns: view.columns,
                    hasAnyAccrualLedgers,
                }),
                handoffColumn: SCHEDULE_HANDOFF_COLUMN,
                amountColumnId: BillColumn.AMOUNT,
                wiring: scheduleWiring,
            }),
        }),
        [hasAnyAccrualLedgers, view, scheduleWiring],
    );

    return (
        <PeriodLockDatesProvider entityIds={entityIds}>
            <ViewTable
                className={classNames(styles.billsView, className)}
                data={data}
                view={visibleView as View}
                config={viewConfig}
                selectedRowId={selectedBillId}
                onRowClick={handleRowClick}
                onFocusedRowChange={handleFocusedRowChange}
                sort={sort}
                onSortChange={onSortChange}
                fixedTopOffsetPx
                emptyView={emptyView}
                tableMeta={tableMeta}
                footer={footer}
                enableKeyboardShortcuts
                rowSelection={rowSelection}
                onRowSelectionChange={onRowSelectionChange}
                onViewChange={onViewChange}
                enableRowSelection={canSelectBill}
                loadMore={loadMore}
            />
        </PeriodLockDatesProvider>
    );
};
